import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const pad = (n) => String(n).padStart(2, '0');

const DEFAULTS = {
  enabled: true,
  target_iso: '2026-09-18T07:30:00+08:00',
  label: 'Tee-off in',
  subtitle: '18 Sept 2026 · Kelab Golf Negara Subang',
  register_url:
    'https://wa.me/60126759467?text=Hi%20EWRF%2C%20I%27d%20like%20to%20register%20for%20the%20Charity%20Golf%202026',
  cta_label: 'Register →',
  show_sparkles: true,
  show_marquee: true,
  marquee_text:
    '⛳  HIT THE GREEN · LIFT A LIFE  ·  EWRF CHARITY GOLF 2026  ·  KELAB GOLF NEGARA SUBANG  ·  18 SEPT 2026 · 7:30 AM TEE-OFF  ·  HOLE-IN-ONE MAZDA CX-30  ·  GRAND PRIZE FLY TO SYDNEY  ·  ',
  live_message: '🏌️ Charity Golf 2026 is LIVE — Tap to register/sponsor',
};

const useCountdown = (targetIso) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const target = new Date(targetIso).getTime();
  const diff = Math.max(0, target - now);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const mins = Math.floor((diff / (1000 * 60)) % 60);
  const secs = Math.floor((diff / 1000) % 60);
  return { days, hours, mins, secs, done: diff <= 0 };
};

const Cell = ({ value, label }) => (
  <div className="flex flex-col items-center leading-none">
    <div
      className="text-2xl md:text-3xl font-black text-white tabular-nums drop-shadow-lg"
      data-testid={`countdown-${label.toLowerCase()}`}
    >
      {pad(value)}
    </div>
    <div className="text-[9px] md:text-[10px] font-bold uppercase tracking-widest text-white/85 mt-0.5">
      {label}
    </div>
  </div>
);

const GolfCountdown = () => {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/content/countdown`);
        if (data && data.id) setCfg({ ...DEFAULTS, ...data });
      } catch {
        // silent — use defaults
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const { days, hours, mins, secs, done } = useCountdown(cfg.target_iso);

  if (!loaded || cfg.enabled === false) return null;

  if (done) {
    return (
      <a
        href={cfg.register_url || '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full bg-gradient-to-r from-emerald-600 to-emerald-500 py-2.5 px-4 text-center shadow-md hover:from-emerald-500 hover:to-emerald-400 transition-colors ewrf-countdown-glow"
        data-testid="golf-countdown-live"
      >
        <span className="text-white text-sm md:text-base font-black uppercase tracking-widest">
          {cfg.live_message || DEFAULTS.live_message}
        </span>
      </a>
    );
  }

  return (
    <div className="w-full ewrf-countdown-glow rounded-none" data-testid="golf-countdown-wrap">
      <a
        href={cfg.register_url || '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full bg-gradient-to-r from-[#0A1628] via-[#0f2137] to-[#1565C0] py-2.5 px-4 shadow-md group hover:from-[#0f2137] hover:via-[#1565C0] hover:to-[#1976D2] transition-all relative overflow-hidden"
        data-testid="golf-countdown"
        aria-label="Register for the EWRF Charity Golf 2026"
      >
        <div className="relative flex items-center justify-center gap-3 md:gap-6">
          {cfg.show_sparkles && (
            <div className="hidden sm:flex items-center gap-1 text-[#D4AF37]">
              <Sparkles className="w-3 h-3 ewrf-sparkle-1" />
              <Sparkles className="w-2.5 h-2.5 ewrf-sparkle-2" />
            </div>
          )}

          <span className="text-[10px] md:text-xs font-black uppercase tracking-[0.25em] text-[#D4AF37] whitespace-nowrap">
            {cfg.label}
          </span>

          <div className="flex items-start gap-2 md:gap-4">
            <Cell value={days} label="Days" />
            <div className="text-white/40 text-xl md:text-2xl font-black leading-none pt-1">:</div>
            <Cell value={hours} label="Hrs" />
            <div className="text-white/40 text-xl md:text-2xl font-black leading-none pt-1">:</div>
            <Cell value={mins} label="Min" />
            <div className="text-white/40 text-xl md:text-2xl font-black leading-none pt-1">:</div>
            <Cell value={secs} label="Sec" />
          </div>

          {cfg.show_sparkles && (
            <div className="hidden sm:flex items-center gap-1 text-[#D4AF37]">
              <Sparkles className="w-2.5 h-2.5 ewrf-sparkle-3" />
              <Sparkles className="w-3 h-3 ewrf-sparkle-1" />
            </div>
          )}

          {cfg.subtitle && (
            <span className="hidden lg:inline text-[10px] font-bold uppercase tracking-widest text-white/70 whitespace-nowrap">
              · {cfg.subtitle}
            </span>
          )}

          {cfg.cta_label && (
            <span
              className="hidden md:inline text-[10px] md:text-xs font-black uppercase tracking-widest text-[#0A1628] bg-[#D4AF37] px-3 py-1 rounded-full shadow ewrf-cta-beat whitespace-nowrap"
              data-testid="golf-countdown-cta"
            >
              {cfg.cta_label}
            </span>
          )}
        </div>
      </a>

      {cfg.show_marquee && cfg.marquee_text && (
        <div className="w-full bg-[#D4AF37] overflow-hidden py-1" data-testid="golf-countdown-marquee">
          <div className="flex whitespace-nowrap ewrf-marquee-track">
            <span className="text-[10px] md:text-xs font-black uppercase tracking-[0.2em] text-[#0A1628] px-2">
              {cfg.marquee_text}
            </span>
            <span
              className="text-[10px] md:text-xs font-black uppercase tracking-[0.2em] text-[#0A1628] px-2"
              aria-hidden="true"
            >
              {cfg.marquee_text}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GolfCountdown;
